import React from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Link,
    makeStyles,
    CircularProgress
} from '@material-ui/core';
import TermsContext from '../context/terms-context';
import AuthContext from '../auth/context';
import { fetchMutationAcceptTerms } from '../hooks/useTermsAcceptance';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
    modal: {
        '& .MuiDialog-paper': {
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            padding: theme.spacing(3),
            minWidth: '400px',
            maxWidth: '500px',
            zIndex: 1400 // Asegurar que esté por encima del AppBar (z-index 1100) y Drawer (z-index 1200)
        },
        '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)', // Fondo más oscuro para el overlay
            zIndex: 1399
        }
    },
    title: {
        color: '#ffffff',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: theme.spacing(2),
        fontSize: '1.5rem'
    },
    message: {
        color: '#ffffff',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: theme.spacing(2),
        fontSize: '1.1rem',
        textAlign: 'center'
    },
    link: {
        color: '#2196f3',
        textDecoration: 'underline',
        cursor: 'pointer',
        display: 'block',
        marginBottom: theme.spacing(3),
        textAlign: 'center',
        '&:hover': {
            color: '#1976d2'
        }
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    cancelButton: {
        backgroundColor: '#9e9e9e',
        color: '#424242',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        '&:hover': {
            backgroundColor: '#757575'
        }
    },
    acceptButton: {
        backgroundColor: '#2196f3',
        color: '#ffffff',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        '&:hover': {
            backgroundColor: '#1976d2'
        },
        '&:disabled': {
            backgroundColor: '#757575',
            color: '#9e9e9e'
        }
    }
}));

const TermsAcceptanceModal = ({ userAuth }) => {
    const classes = useStyles();
    const { hasAcceptedTerms, setHasAcceptedTerms } = React.useContext(TermsContext);
    const { logout } = React.useContext(AuthContext);
    const [termsUrl] = React.useState(
        process.env.REACT_APP_VALIDATE_AVISOS_CONDICIONES_URL || '#'
    );
    const [hasViewedTerms, setHasViewedTerms] = React.useState(false);

    const [mutate, { isLoading: isAccepting }] = useMutation(fetchMutationAcceptTerms, {
        onSuccess: () => {
            setHasAcceptedTerms(true);
            toast.success('Términos y condiciones aceptados correctamente', {
                autoClose: 2000
            });
        },
        onError: (err) => {
            toast.error(err?.response?.data?.Message || 'Error al aceptar los términos y condiciones', {
                autoClose: 5000
            });
        }
    });

    const isOpen = hasAcceptedTerms === false;

    const handleAccept = () => {
        const tipoComunicado = 1;
        const data = {
            cveEmpleadoAviso: 0,
            cveEmpleado: userAuth.empleadoId,
            TipoComunicado: tipoComunicado,
            FechaWeb: new Date().toISOString(),
        };
        mutate(data);
    };

    const handleCancel = () => {
        logout();
        setHasAcceptedTerms(null);
        setTimeout(() => {
            window.location.href = "/sign-in";
        }, 100);
    };

    const handleTermsLink = (e) => {
        e.preventDefault();
        if (termsUrl && termsUrl !== '#') {
            window.open(termsUrl, '_blank');
            setHasViewedTerms(true);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            disableEscapeKeyDown
            className={classes.modal}
            aria-labelledby="terms-dialog-title"
            style={{ zIndex: 1400 }}
        >
            <DialogContent>
                <Typography id="terms-dialog-title" className={classes.title}>
                    AVISO
                </Typography>
                <Typography className={classes.message}>
                    PENDIENTE DE ACEPTAR TÉRMINOS Y CONDICIONES
                </Typography>
                <Link
                    className={classes.link}
                    onClick={handleTermsLink}
                    component="button"
                    variant="body1"
                >
                    Revisar Términos y Condiciones
                </Link>
            </DialogContent>
            <DialogActions className={classes.buttonContainer}>
                <Button
                    onClick={handleCancel}
                    className={classes.cancelButton}
                    variant="contained"
                    disabled={isAccepting}
                >
                    CANCELAR
                </Button>
                <Button
                    onClick={handleAccept}
                    className={classes.acceptButton}
                    variant="contained"
                    disabled={isAccepting || !hasViewedTerms}
                >
                    {isAccepting ? (
                        <>
                            <CircularProgress size={20} style={{ marginRight: 8, color: '#fff' }} />
                            PROCESANDO...
                        </>
                    ) : (
                        'ACEPTAR'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

TermsAcceptanceModal.propTypes = {
    userAuth: PropTypes.object.isRequired
};

export default TermsAcceptanceModal;

